/**
 * VendorFormPopup — 자재파트너 등록 팝업
 *
 * 기존 VendorFormPage(G-2)와 동일한 폼 구성·API 호출.
 * 등록 전용 (편집·더추가 미지원). 자재파트너는 항상 사업자(중복판정=사업자번호).
 *
 * 사용:
 *   import { VendorFormPopup } from '@indipia/shared-features'
 *   <VendorFormPopup onClose={() => setOpen(false)} onSuccess={(id) => reload()} />
 */
import { useEffect, useId, useMemo, useState, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Input, MultiSelect, Segmented, Textarea } from '@indipia/ui-core';
import { SIGUNGU_LIST, COMM_METHOD_OPTIONS, COMM_METHOD_HELP_MATERIAL } from '@indipia/shared';
import { createVendor, checkVendorExists, fetchWorkTypes, fetchCommonCodes } from './api';
import type { VendorFormPopupProps } from './types';

// 배송지역 시군구 옵션(전체) + 코드→명 조회 — @indipia/shared 정적 데이터
const SIGUNGU_OPTIONS = SIGUNGU_LIST.map((s) => ({ value: s.code, label: s.name }));
const SIGUNGU_NAME_BY_CODE = new Map(SIGUNGU_LIST.map((s) => [s.code, s.name]));

// ── 타입 ──────────────────────────────────────────────────────────────────────

interface FormEntry {
  vendorName: string;      // 상호명
  bizNo: string;           // 사업자번호
  repName: string;         // 대표자명
  contactName: string;     // 담당자명
  phone: string;           // 담당자 연락처
  email: string;
  dealStatus: string;      // 거래 상태 (공통코드 E01_PARTNER_DEAL_STATUS: ACTIVE/SUSPENDED)
  commYn: boolean;         // 연락 방식 (true=알림톡 발송-회신 · false=결과만 직접등록, 기본 true)
  workCodes: string[];     // 거래품목 공종 코드(다중)
  dealItem: string;        // 세부취급품목(자유텍스트)
  sigunguCodes: string[];  // 배송지역 시군구 코드(다중)
  memo: string;
}

const EMPTY_FORM: FormEntry = {
  vendorName: '',
  bizNo: '',
  repName: '',
  contactName: '',
  phone: '',
  email: '',
  dealStatus: 'ACTIVE',    // 거래 상태 기본값 = 거래중
  commYn: true,            // 연락 방식 기본값 = 알림톡 발송-회신
  workCodes: [],
  dealItem: '',
  sigunguCodes: [],
  memo: '',
};

type BizNoCheckStatus = 'idle' | 'checking' | 'ok' | 'duplicate';

// ── 헬퍼 ──────────────────────────────────────────────────────────────────────

function formatBizNo(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
}

function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.startsWith('02')) {
    const d = digits.slice(0, 10);
    if (d.length <= 2) return d;
    if (d.length <= 5) return `${d.slice(0, 2)}-${d.slice(2)}`;
    if (d.length <= 9) return `${d.slice(0, 2)}-${d.slice(2, 5)}-${d.slice(5)}`;
    return `${d.slice(0, 2)}-${d.slice(2, 6)}-${d.slice(6)}`;
  }
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (digits.length <= 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(form: FormEntry): string | null {
  if (!form.vendorName.trim()) return '상호명을 입력해주세요.';
  if (!form.bizNo.trim()) return '사업자번호를 입력해주세요.';
  if (!form.repName.trim()) return '대표자명을 입력해주세요.';
  if (!form.contactName.trim()) return '담당자명을 입력해주세요.';
  if (!form.phone.trim()) return '담당자 연락처를 입력해주세요.';
  if (form.email.trim() && !EMAIL_RE.test(form.email.trim())) return '이메일 형식이 올바르지 않습니다.';
  if (form.workCodes.length === 0) return '거래 품목을 선택해주세요.';
  return null;
}

function toPayload(form: FormEntry) {
  return {
    vendorTypeCode: 'MATERIAL' as const,
    vendorName: form.vendorName.trim(),
    vendorBizNo: form.bizNo.trim() || null,
    vendorRepName: form.repName.trim() || null,
    contactName: form.contactName.trim() || null,
    vendorPhone: form.phone.trim(),
    vendorEmail: form.email.trim() || null,
    dealStatus: form.dealStatus || null,
    commYn: form.commYn ? 'Y' : 'N',
    workTypes: form.workCodes,
    dealItem: form.dealItem.trim() || null,
    sigungus: form.sigunguCodes.map((c) => ({ sigunguCode: c, sigunguName: SIGUNGU_NAME_BY_CODE.get(c) ?? c })),
    vendorMemo: form.memo.trim() || null,
  };
}

const INPUT_CLS = 'placeholder:text-text-muted focus:border-group focus:ring-0';

// 라벨-입력 한 줄 정렬용 2열 그리드(라벨 고정폭 + 입력). 모든 행 동일 구조로 좌측 정렬 일치.
const ROW_GRID: CSSProperties = { display: 'grid', gridTemplateColumns: '6rem 1fr', alignItems: 'center', columnGap: '12px' };

// ── 컴포넌트 ──────────────────────────────────────────────────────────────────

export function VendorFormPopup({ onClose, onSuccess }: VendorFormPopupProps) {
  const queryClient = useQueryClient();
  const { data: workTypes = [] } = useQuery({
    queryKey: ['work-types'],
    queryFn: fetchWorkTypes,
  });

  // 거래 상태 옵션 — 공통코드 E01_PARTNER_DEAL_STATUS (ACTIVE/SUSPENDED) · 시공파트너와 공용
  const { data: dealStatusCodes = [] } = useQuery({
    queryKey: ['common-codes', 'E01_PARTNER_DEAL_STATUS'],
    queryFn: () => fetchCommonCodes('E01_PARTNER_DEAL_STATUS'),
  });
  const dealStatusOptions = useMemo(
    () => dealStatusCodes.map((c) => ({ value: c.codeValue, label: c.codeName })),
    [dealStatusCodes],
  );

  const [form, setForm] = useState<FormEntry>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  // 사업자번호 실시간 중복 확인(디바운스)
  const [dupStatus, setDupStatus] = useState<BizNoCheckStatus>('idle');
  useEffect(() => {
    const digits = form.bizNo.replace(/\D/g, '');
    if (digits.length < 10) { setDupStatus('idle'); return; }
    setDupStatus('checking');
    const t = setTimeout(() => {
      checkVendorExists(form.bizNo)
        .then((exists) => setDupStatus(exists ? 'duplicate' : 'ok'))
        .catch(() => setDupStatus('idle'));
    }, 400);
    return () => clearTimeout(t);
  }, [form.bizNo]);

  const idPrefix = useId();
  const titleId = `${idPrefix}-title`;
  const vendorNameId = `${idPrefix}-vendorName`;
  const bizNoId = `${idPrefix}-bizNo`;
  const repNameId = `${idPrefix}-repName`;
  const contactNameId = `${idPrefix}-contactName`;
  const phoneId = `${idPrefix}-phone`;
  const emailId = `${idPrefix}-email`;
  const dealItemId = `${idPrefix}-dealItem`;
  const memoId = `${idPrefix}-memo`;

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const saveMutation = useMutation({
    mutationFn: () => createVendor(toPayload(form)),
    onSuccess: (vendorId) => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      onSuccess?.(vendorId);
      onClose();
    },
    onError: () => setFormError('저장에 실패했습니다. 다시 시도해주세요.'),
  });

  const dupBlocking = dupStatus === 'duplicate';

  function handleSave() {
    const err = validate(form);
    if (err) { setFormError(err); return; }
    if (dupBlocking) { setFormError('이미 등록된 사업자번호입니다.'); return; }
    setFormError(null);
    saveMutation.mutate();
  }

  // 거래품목 공종 옵션 (MultiSelect 용)
  const workTypeOptions = workTypes.map((w) => ({ value: w.workTypeCode, label: w.workTypeName }));

  return (
    <div
      className="fixed inset-0 z-modal flex items-center justify-center"
      style={{ background: 'var(--overlay-scrim)' }}
    >
      <div
        className="relative flex max-h-[92vh] w-full max-w-xl flex-col overflow-hidden rounded-xl bg-surface-raised shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >

        {/* 헤더 */}
        <div className="flex items-start justify-between border-b border-border px-[22px] py-4">
          <div>
            <h2 id={titleId} className="text-base font-bold text-text">자재파트너 등록</h2>
            <p className="mt-0.5 text-[length:var(--type-caption-size)] text-text-muted">
              함께 일할 자재업체 또는 거래처를 등록하세요
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-sm bg-transparent text-[18px] leading-none text-text-muted hover:bg-surface-subtle"
            aria-label="닫기"
          >
            ×
          </button>
        </div>

        {/* 폼 콘텐츠 */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          <div>
            <p className="mb-3.5 text-xs font-bold uppercase tracking-widest text-text-muted">
              기본 정보
            </p>

            {/* 1구역: 상호명 · 사업자번호 · 대표자명 */}
            <div className="border-b border-border pb-2">
              <div className="py-1.5" style={ROW_GRID}>
                <label htmlFor={vendorNameId} className="text-xs font-semibold text-text-secondary">상호명 <span className="text-error">*</span></label>
                <Input
                  id={vendorNameId}
                  value={form.vendorName}
                  onChange={(e) => setForm((f) => ({ ...f, vendorName: e.target.value }))}
                  placeholder="예) (주)인디피아 자재"
                  className={INPUT_CLS}
                />
              </div>
              <div className="py-1.5" style={ROW_GRID}>
                <label htmlFor={bizNoId} className="text-xs font-semibold text-text-secondary">사업자번호 <span className="text-error">*</span></label>
                <div>
                  <Input
                    id={bizNoId}
                    value={form.bizNo}
                    onChange={(e) => setForm((f) => ({ ...f, bizNo: formatBizNo(e.target.value) }))}
                    placeholder="000-00-00000"
                    className={INPUT_CLS}
                  />
                  {dupStatus === 'checking' && <p className="mt-1.5 text-xs text-text-muted">중복 확인 중…</p>}
                  {dupStatus === 'ok' && <p className="mt-1.5 text-xs font-semibold text-success">✓ 등록 가능한 사업자번호예요</p>}
                  {dupStatus === 'duplicate' && <p className="mt-1.5 text-xs font-semibold text-error">이미 등록된 사업자예요</p>}
                </div>
              </div>
              <div className="py-1.5" style={ROW_GRID}>
                <label htmlFor={repNameId} className="text-xs font-semibold text-text-secondary">대표자명 <span className="text-error">*</span></label>
                <Input
                  id={repNameId}
                  value={form.repName}
                  onChange={(e) => setForm((f) => ({ ...f, repName: e.target.value }))}
                  placeholder="대표자명 입력"
                  className={INPUT_CLS}
                />
              </div>
            </div>

            {/* 2구역: 담당자명 · 담당자 연락처 · 이메일 */}
            <div className="border-b border-border pb-2">
              <div className="py-1.5" style={ROW_GRID}>
                <label htmlFor={contactNameId} className="text-xs font-semibold text-text-secondary">담당자명 <span className="text-error">*</span></label>
                <Input
                  id={contactNameId}
                  value={form.contactName}
                  onChange={(e) => setForm((f) => ({ ...f, contactName: e.target.value }))}
                  placeholder="예) 홍길동"
                  className={INPUT_CLS}
                />
              </div>
              <div className="py-1.5" style={ROW_GRID}>
                <label htmlFor={phoneId} className="text-xs font-semibold text-text-secondary">담당자 연락처 <span className="text-error">*</span></label>
                <div>
                  <Input
                    id={phoneId}
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: formatPhone(e.target.value) }))}
                    placeholder="010-0000-0000"
                    className={INPUT_CLS}
                  />
                  <p className="mt-1 text-xs text-text-muted">자재 발주정보를 카톡으로 보내는 번호예요</p>
                </div>
              </div>
              <div className="py-1.5" style={ROW_GRID}>
                <label htmlFor={emailId} className="text-xs font-semibold text-text-secondary">이메일</label>
                <Input
                  id={emailId}
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="예) contact@company.com"
                  className={INPUT_CLS}
                />
              </div>
              <div className="py-1.5" style={ROW_GRID}>
                <label className="text-xs font-semibold text-text-secondary">거래 상태 <span className="text-error">*</span></label>
                <div>
                  <Segmented
                    value={form.dealStatus}
                    onChange={(v) => setForm((f) => ({ ...f, dealStatus: v }))}
                    options={dealStatusOptions}
                  />
                  <p className="mt-1 text-xs text-text-muted">거래중지면 공사스케줄 자동매핑 대상에서 빠져요</p>
                </div>
              </div>
              {/* 연락 방식 */}
              <div className="py-1.5" style={ROW_GRID}>
                <label className="text-xs font-semibold text-text-secondary">연락 방식 <span className="text-error">*</span></label>
                <div>
                  <Segmented
                    value={form.commYn ? 'Y' : 'N'}
                    onChange={(v) => setForm((f) => ({ ...f, commYn: v === 'Y' }))}
                    options={COMM_METHOD_OPTIONS}
                  />
                  <p className="mt-1 text-xs text-text-muted">{COMM_METHOD_HELP_MATERIAL}</p>
                </div>
              </div>
            </div>

            {/* 3구역: 거래품목 · 세부취급품목 */}
            <div className="border-b border-border pb-2">
              <div className="py-1.5" style={ROW_GRID}>
                <label className="text-xs font-semibold text-text-secondary">거래 품목 <span className="text-error">*</span></label>
                <div>
                  <MultiSelect
                    values={form.workCodes}
                    onChange={(v) => setForm((f) => ({ ...f, workCodes: v }))}
                    options={workTypeOptions}
                    searchable
                    placeholder="공종 선택 (여러 개)"
                  />
                  <p className="mt-1 text-xs text-text-muted">공사스케줄에서 이 공종의 자재파트너를 자동으로 매핑해요</p>
                  <p className="mt-1 text-xs text-text-muted">주거래처 지정은 스튜디오 설정 › 발주-입고설정에서 해요</p>
                </div>
              </div>
              <div className="py-1.5" style={ROW_GRID}>
                <label htmlFor={dealItemId} className="text-xs font-semibold text-text-secondary">세부취급품목</label>
                <div>
                  <Input
                    id={dealItemId}
                    value={form.dealItem}
                    onChange={(e) => setForm((f) => ({ ...f, dealItem: e.target.value }))}
                    placeholder="예) 수입타일, 욕실자재"
                    className={INPUT_CLS}
                  />
                  <p className="mt-1 text-xs text-text-muted">참고 정보로 추천 팝업에 함께 보여요</p>
                </div>
              </div>
            </div>

            {/* 4구역: 배송지역 */}
            <div className="border-b border-border pb-2">
              <div className="py-2.5">
                <label className="mb-1.5 block text-xs font-semibold text-text-secondary">배송지역</label>
                <MultiSelect
                  values={form.sigunguCodes}
                  onChange={(v) => setForm((f) => ({ ...f, sigunguCodes: v }))}
                  options={SIGUNGU_OPTIONS}
                  searchable
                  placeholder="시·구 검색 (예: 강남)"
                />
                <p className="mt-1 text-xs text-text-muted">추천 팝업에 함께 보여요</p>
              </div>
            </div>

            {/* 5구역: 메모 */}
            <div className="border-b border-border pb-2">
              <div className="py-2.5">
                <label htmlFor={memoId} className="mb-1.5 block text-xs font-semibold text-text-secondary">메모</label>
                <Textarea
                  id={memoId}
                  value={form.memo}
                  onChange={(e) => setForm((f) => ({ ...f, memo: e.target.value }))}
                  placeholder="특이사항이나 메모를 입력하세요"
                  rows={4}
                  className="max-w-none"
                />
                <p className="mt-1 text-xs text-text-muted">첫 줄이 자재파트너 추천 팝업에 보여요</p>
              </div>
            </div>
          </div>

          {/* 에러 */}
          {formError && (
            <p className="mt-3 text-xs font-semibold text-error">{formError}</p>
          )}

          {/* 저장 버튼 */}
          <Button
            variant="primary"
            size="lg"
            type="button"
            onClick={handleSave}
            disabled={saveMutation.isPending || dupBlocking}
            loading={saveMutation.isPending}
            className="mt-4 w-full"
          >
            {saveMutation.isPending ? '저장 중…' : '저장'}
          </Button>
        </div>
      </div>
    </div>
  );
}
